import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ChatDrawer.css";
import { NO_TRACK, chatApi, errText, durationText, trackLabel } from "./api";
import type { LogEntry, Message, QueueRow, Session, TaskEvent, TaskRow } from "./api";
import { useChatDock } from "./dock";
import { Alert, EmptyState, Loading } from "../components/Card";
import { Button } from "../components/Button";
import { Chip, STATUS } from "../components/Status";
import type { StatusKey } from "../components/Status";
import { Icon } from "../components/Icon";
import { StageStrip, stageCells } from "../components/StageStrip";
import { FOOTPRINT_TAB, SessionTabs, sessionGlyph, sessionMeta } from "../components/SessionTabs";
import { FootprintRow, HumanRow, LogRow, SessionNote } from "../components/ChatRows";
import { ChatInput } from "../components/ChatInput";
import { tx } from "../i18n";

/* ══ 채팅 겉창 — 채택 시안 안 B(Figma 38-2, 사람 확정 2026-09-13) ══
   층은 하나다: **이슈 › 단계 › 세션.** 위에서 이슈를 고르고, 단계 띠의 칸을 누르면 그 단계의
   세션이 탭으로 선다. 「이 이슈가 어디까지 왔나」와 「그 단계에 무엇이 돌았나」가 한 자리다.

   **이 화면은 API 를 만들지 않고 판정도 하지 않는다.** 말/진행은 서버가 접은 `role`·`kind`,
   단계는 세션 기록의 `track` 과 큐의 `track`, 전달은 우편함 행의 사실 셋, 「도는 중」은
   `endedAt === null`. 화면이 하는 유일한 배치는 사람의 말을 **끝에** 붙이는 것이다 —
   접힌 로그 줄엔 시각이 없어 사이에 끼우면 순서를 지어내게 된다.

   요청은 **열려 있고 탭이 보일 때만** 간다. 닫히면 타이머가 없다. */

const FOLLOW_MS = 3000;
/** 몇 박동마다 세션 행(끝났나 · 멈춤 표)을 다시 읽나. */
const SESSION_EVERY = 3;

type LogState = {
  entries: LogEntry[];
  first: number | null;
  last: number | null;
  total: number;
  hasPrev: boolean;
};

function bySessionStart(a: Session, b: Session): number {
  return (a.startedAt ?? "").localeCompare(b.startedAt ?? "") || a.id.localeCompare(b.id);
}

/** 이슈 고르기의 순서 — 도는 것 먼저, 그다음 최근에 세션이 돌았던 이슈, 그다음 열린 이슈.
 *  닫힌 이슈는 세션이 있었을 때만 선다(사람 세션만 돈 닫힌 이슈는 이력 화면의 몫이다). */
export function orderTasks(tasks: TaskRow[], sessions: Session[]): TaskRow[] {
  const latest = new Map<string, string>();
  const running = new Set<string>();
  for (const s of sessions) {
    if (s.endedAt === null) running.add(s.taskId);
    const at = s.startedAt ?? "";
    if ((latest.get(s.taskId) ?? "") < at) latest.set(s.taskId, at);
  }
  const closed = (t: TaskRow) => t.status === "done" || t.status === "cancelled";
  const rank = (t: TaskRow) => (running.has(t.id) ? 0 : latest.has(t.id) ? 1 : 2);
  return tasks
    .filter((t) => !closed(t) || latest.has(t.id))
    .sort((a, b) => {
      const r = rank(a) - rank(b);
      if (r !== 0) return r;
      if (rank(a) < 2) return (latest.get(b.id) ?? "").localeCompare(latest.get(a.id) ?? "");
      return (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "");
    });
}

function statusLabel(s: string): string {
  const k = (STATUS as Record<string, { label: string; glyph: string }>)[s as StatusKey];
  return k ? `${k.glyph} ${k.label}` : s;
}

export function ChatDrawer() {
  const dock = useChatDock();
  const { open, target, closeDock, reportSessions } = dock;

  const [tasks, setTasks] = useState<TaskRow[] | null>(null);
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [queue, setQueue] = useState<QueueRow[] | null>(null);
  const [listErr, setListErr] = useState<string | null>(null);

  const [taskId, setTaskId] = useState<string | null>(null);
  const [events, setEvents] = useState<TaskEvent[] | null>(null);
  const [evErr, setEvErr] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[] | null>(null);

  const [stage, setStage] = useState<string | null>(null);
  const [tab, setTab] = useState<string>(FOOTPRINT_TAB);

  const [log, setLog] = useState<LogState | null>(null);
  const [logErr, setLogErr] = useState<string | null>(null);
  const [prevBusy, setPrevBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [actErr, setActErr] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const scrollRef = useRef<HTMLDivElement>(null);
  const stickBottom = useRef(true);
  const rootRef = useRef<HTMLElement>(null);

  /* ── 목록 읽기 — 겉창이 열릴 때, 그리고 사람이 ↻ 를 누를 때 ── */
  const loadLists = useCallback(async () => {
    setListErr(null);
    try {
      const [t, s, q] = await Promise.all([
        chatApi.tasks(),
        chatApi.sessions(undefined, 200),
        chatApi.queue().catch(() => [] as QueueRow[]),
      ]);
      setTasks(t);
      setSessions(s);
      setQueue(q);
      reportSessions(s);
    } catch (e) {
      setListErr(errText(e));
    }
  }, [reportSessions]);

  useEffect(() => {
    if (!open) return;
    setNow(Date.now());
    void loadLists();
  }, [open, loadLists]);

  /* ── Esc 로 닫는다. capture — 안쪽 요소가 먼저 삼키면 닫는 길이 하나 준다. 밖을 눌러도
     안 닫힌다: 왼쪽 화면이 살아 있는 것이 이 겉창의 값이고, 입력칸에 쓰던 말이 날아간다. ── */
  useEffect(() => {
    if (!open) return;
    const on = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        ev.stopPropagation();
        closeDock();
      }
    };
    document.addEventListener("keydown", on, true);
    rootRef.current?.focus();
    return () => document.removeEventListener("keydown", on, true);
  }, [open, closeDock]);

  const ordered = useMemo(() => (tasks && sessions ? orderTasks(tasks, sessions) : []), [tasks, sessions]);

  /* 목록이 오면 무엇을 볼지 고른다 — 가리킨 것(target)이 있으면 그것, 없으면 첫 줄. */
  useEffect(() => {
    if (!sessions || !tasks) return;
    let next: string | null = null;
    if (target.sessionId) next = sessions.find((s) => s.id === target.sessionId)?.taskId ?? null;
    if (!next && target.taskId) next = target.taskId;
    if (!next && !taskId) next = ordered[0]?.id ?? null;
    if (next && next !== taskId) setTaskId(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, tasks, target]);

  const taskSessions = useMemo(
    () => (sessions && taskId ? sessions.filter((s) => s.taskId === taskId).sort(bySessionStart) : []),
    [sessions, taskId],
  );
  const nextTrack = useMemo(
    () => (queue && taskId ? (queue.find((r) => r.task.id === taskId)?.track ?? null) : null),
    [queue, taskId],
  );
  const cells = useMemo(() => stageCells(taskSessions, nextTrack), [taskSessions, nextTrack]);
  const task = useMemo(() => tasks?.find((t) => t.id === taskId) ?? null, [tasks, taskId]);

  /* ── 이슈가 바뀌면: 이력·우편함을 읽고 단계·탭의 기본값을 고른다 ── */
  useEffect(() => {
    if (!open || !taskId) return;
    setEvents(null);
    setEvErr(null);
    setMessages(null);
    setActErr(null);
    chatApi
      .events(taskId)
      .then(setEvents)
      .catch((e) => setEvErr(errText(e)));
    chatApi
      .messages(taskId)
      .then(setMessages)
      .catch(() => setMessages(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, taskId]);

  useEffect(() => {
    if (!taskId) return;
    const running = taskSessions.find((s) => s.endedAt === null);
    const latest = [...taskSessions].reverse()[0];
    const want = target.sessionId ? taskSessions.find((s) => s.id === target.sessionId) : undefined;
    const pick = want ?? running ?? latest;
    const st = pick?.track ?? nextTrack ?? "run";
    setStage(st);
    setTab(pick && (pick.track ?? NO_TRACK) === st ? pick.id : FOOTPRINT_TAB);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, taskSessions.length]);

  const stageSessions = useMemo(
    () => taskSessions.filter((s) => (s.track ?? NO_TRACK) === stage),
    [taskSessions, stage],
  );
  const session = useMemo(() => taskSessions.find((s) => s.id === tab) ?? null, [taskSessions, tab]);

  const pickStage = (key: string) => {
    setStage(key);
    const mine = taskSessions.filter((s) => (s.track ?? NO_TRACK) === key);
    const running = mine.find((s) => s.endedAt === null);
    const latest = [...mine].reverse()[0];
    setTab((running ?? latest)?.id ?? FOOTPRINT_TAB);
  };

  /* ── 세션 탭이 바뀌면 꼬리 창을 읽는다 ── */
  useEffect(() => {
    if (!open || !session) {
      setLog(null);
      setLogErr(null);
      return;
    }
    let alive = true;
    setLog(null);
    setLogErr(null);
    stickBottom.current = true;
    chatApi
      .logTail(session.id)
      .then((w) => {
        if (!alive) return;
        setLog({ entries: w.entries, first: w.first, last: w.last, total: w.total, hasPrev: w.hasPrev });
      })
      .catch((e) => alive && setLogErr(errText(e)));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, session?.id]);

  /* ── 따라가기: 열려 있고 · 탭이 보이고 · 고른 세션이 도는 중일 때만 박동한다.
     닫히거나 숨으면 타이머가 아예 없다 — 「요청이 0」이 수용 기준이다. ── */
  const lastRef = useRef<number | null>(null);
  lastRef.current = log?.last ?? null;
  const sessionOpen = session?.endedAt === null;
  useEffect(() => {
    if (!open || !session || !sessionOpen) return;
    const id = session.id;
    let beat = 0;
    let busy = false;
    const tick = async () => {
      if (document.hidden || busy) return;
      busy = true;
      beat += 1;
      try {
        const after = lastRef.current ?? 0;
        const w = await chatApi.logAfter(id, after);
        if (w.entries.length > 0) {
          setLog((cur) =>
            cur
              ? { ...cur, entries: [...cur.entries, ...w.entries], last: w.last ?? cur.last, total: w.total }
              : { entries: w.entries, first: w.first, last: w.last, total: w.total, hasPrev: w.hasPrev },
          );
          // 줄이 늘었다 = 세션이 무언가 했다 — 우편함을 집어 갔을 수 있으니 전달 상태를 다시 읽는다.
          if (taskId) chatApi.messages(taskId).then(setMessages).catch(() => undefined);
        }
        if (beat % SESSION_EVERY === 0) {
          const s = await chatApi.session(id);
          setSessions((cur) => (cur ? cur.map((x) => (x.id === s.id ? s : x)) : cur));
          if (s.endedAt !== null) {
            // 끝났다 — 목록을 다시 읽어 단추의 수와 이슈 순서를 맞춘다.
            void loadLists();
            if (taskId) chatApi.events(taskId).then(setEvents).catch(() => undefined);
          }
        }
        setNow(Date.now());
      } catch {
        // 따라가기의 실패는 조용히 — 다음 박동이 다시 간다. 첫 읽기의 실패는 위에서 말한다.
      } finally {
        busy = false;
      }
    };
    const timer = setInterval(tick, FOLLOW_MS);
    const onVis = () => {
      if (!document.hidden) void tick();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [open, session?.id, sessionOpen, taskId, loadLists]);

  /* 새 줄이 붙으면 바닥에 있던 사람만 따라 내려간다 — 위로 올려 읽는 중이면 안 건드린다. */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !stickBottom.current) return;
    el.scrollTop = el.scrollHeight;
  }, [log?.entries.length, messages?.length, events?.length, tab]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };

  const loadPrev = async () => {
    if (!session || !log || log.first === null) return;
    setPrevBusy(true);
    const el = scrollRef.current;
    const before = el ? el.scrollHeight - el.scrollTop : 0;
    try {
      const w = await chatApi.logBefore(session.id, log.first);
      setLog((cur) =>
        cur ? { ...cur, entries: [...w.entries, ...cur.entries], first: w.first ?? cur.first, hasPrev: w.hasPrev } : cur,
      );
      requestAnimationFrame(() => {
        if (el) el.scrollTop = el.scrollHeight - before;
      });
    } catch (e) {
      setActErr(errText(e));
    } finally {
      setPrevBusy(false);
    }
  };

  const send = async (text: string) => {
    if (!taskId) return;
    setSending(true);
    setActErr(null);
    try {
      const m = await chatApi.leave(taskId, text);
      setMessages((cur) => [...(cur ?? []), m]);
      stickBottom.current = true;
    } catch (e) {
      setActErr(tx("chat.sendFailed", { err: errText(e) }));
      throw e;
    } finally {
      setSending(false);
    }
  };

  const stop = async () => {
    if (!session) return;
    setActErr(null);
    try {
      const s = await chatApi.stop(session.id);
      setSessions((cur) => (cur ? cur.map((x) => (x.id === s.id ? s : x)) : cur));
    } catch (e) {
      setActErr(tx("chat.stopFailed", { err: errText(e) }));
    }
  };

  if (!open) return null;

  /* 이 세션에게 남긴 말과 세션 없이 남긴 말을 가른다 — 후자는 사람 세션(발자국 탭)의 것이다. */
  const sessionMsgs = (messages ?? []).filter((m) => session && m.sessionId === session.id);
  const issueMsgs = (messages ?? []).filter((m) => m.sessionId === null);
  const runningCount = sessions ? sessions.filter((s) => s.endedAt === null).length : null;
  const who = session ? `${tx("issue.heldShort")}${session.vendor ? ` · ${session.vendor}` : ""}` : tx("issue.heldShort");

  return (
    <aside
      className="cdock"
      role="dialog"
      aria-label={tx("chat.label")}
      ref={rootRef}
      tabIndex={-1}
      data-testid="chat-drawer"
    >
      <header className="cdock__head">
        <label className="cdock__picklabel">
          <span className="cdock__picktext">{tx("chat.issue")}</span>
          {tasks && ordered.length === 0 ? (
            /* 고를 것이 없으면 드롭다운을 안 그린다 — 「이슈 없음」 한 줄짜리 선택 상자는 고장으로
               읽힌다(사람 확인 2026-09-13). */
            <span className="b-badge">{tx("chat.noIssue")}</span>
          ) : (
            <span className="b-select-wrap cdock__pick">
            <select
              className="b-select is-md"
              value={taskId ?? ""}
              aria-label={tx("chat.pickIssue")}
              onChange={(e) => setTaskId(e.target.value || null)}
              disabled={!tasks}
            >
              {!tasks ? <option value="">{tx("board.loading")}</option> : null}
              {ordered.map((t) => (
                <option key={t.id} value={t.id}>
                  {statusLabel(t.status)} · {t.title} ({t.id})
                </option>
              ))}
            </select>
              <Icon name="chevronDown" />
            </span>
          )}
        </label>
        <Chip tone={runningCount ? "success" : "muted"}>
          {runningCount === null ? tx("chat.runningUnknown") : tx("nav.chat.running", { n: runningCount })}
        </Chip>
        <Button size="md" variant="ghost" title={tx("chat.reload")} aria-label={tx("chat.reload")} onClick={() => void loadLists()}>
          ↻
        </Button>
        <Button size="md" variant="ghost" title={tx("chat.closeEsc")} aria-label={tx("chat.close")} onClick={closeDock}>
          <Icon name="x" />
        </Button>
      </header>

      {listErr ? (
        <div className="cdock__pad">
          <Alert tone="error">{tx("chat.listFailed", { err: listErr })}</Alert>
        </div>
      ) : null}

      {taskId ? (
        <>
          <div className="cdock__strip">
            <StageStrip cells={cells} selected={stage} onSelect={pickStage} />
          </div>
          <div className="cdock__tabs">
            <SessionTabs sessions={stageSessions} selected={tab} onSelect={setTab} footprintCount={events ? events.length : null} />
          </div>
        </>
      ) : null}

      <div className="cdock__scroll" ref={scrollRef} onScroll={onScroll}>
        {!taskId && tasks && ordered.length === 0 ? (
          <EmptyState
            title={tx("chat.empty.title")}
            desc={tx("chat.empty.desc")}
          />
        ) : null}

        {taskId && session ? (
          <>
            <div className="cdock__shead">
              <span className={`cdock__sglyph cdock__sglyph--${sessionGlyph(session).tone}`} aria-hidden="true">
                {sessionGlyph(session).glyph}
              </span>
              <b>{sessionGlyph(session).word}</b> · {trackLabel(session.track)} · {sessionMeta(session, now)}
              {session.endedAt ? ` · ${durationText(session.startedAt, session.endedAt)}` : ""}
              {" · "}
              {tx("chat.lines", { n: session.lineCount })} · {tx("chat.tokens")}{" "}
              {session.inputTokens === null && session.outputTokens === null
                ? tx("chat.unknown")
                : `${session.inputTokens ?? "?"} / ${session.outputTokens ?? "?"}`}
            </div>
            {logErr ? (
              <Alert tone="error">{tx("chat.logFailed", { err: logErr })}</Alert>
            ) : log === null ? (
              <Loading className="cdock__pad" />
            ) : log.total === 0 ? (
              <EmptyState
                title={tx("chat.noLog.title")}
                desc={tx("chat.noLog.desc")}
              />
            ) : (
              <>
                {log.hasPrev ? (
                  <div className="cdock__prev">
                    <Button size="sm" variant="ghost" loading={prevBusy} onClick={() => void loadPrev()}>
                      {tx("chat.more", { total: log.total, shown: log.entries.length })}
                    </Button>
                  </div>
                ) : null}
                {log.entries.map((e) => (
                  <LogRow key={e.seq} e={e} who={who} />
                ))}
              </>
            )}
            {session.endedAt ? <SessionNote track={session.track} note={session.note} /> : null}
            {sessionMsgs.map((m) => (
              <HumanRow key={m.id} m={m} />
            ))}
          </>
        ) : null}

        {taskId && !session ? (
          <>
            <p className="caption cdock__pad">{tx("chat.footprintLead")}</p>
            {evErr ? (
              <Alert tone="error">{tx("chat.eventsFailed", { err: evErr })}</Alert>
            ) : events === null ? (
              <Loading className="cdock__pad" />
            ) : events.length === 0 ? (
              <EmptyState title={tx("chat.noFootprint.title")} desc={tx("chat.noFootprint.desc")} />
            ) : (
              events.map((e, i) => <FootprintRow key={`${e.at}-${i}`} e={e} />)
            )}
            {issueMsgs.map((m) => (
              <HumanRow key={m.id} m={m} />
            ))}
          </>
        ) : null}

        {actErr ? <Alert tone="error">{actErr}</Alert> : null}
      </div>

      <footer className="cdock__foot">
        {taskId && session && session.endedAt === null ? (
          <ChatInput
            hint={
              session.stopRequestedAt
                ? tx("chat.hint.stopping")
                : tx("chat.hint.running")
            }
            onSend={send}
            onStop={stop}
            stopping={Boolean(session.stopRequestedAt)}
            sending={sending}
          />
        ) : null}
        {taskId && session && session.endedAt !== null ? (
          <p className="caption">{tx("chat.hint.ended")}</p>
        ) : null}
        {taskId && !session ? (
          <ChatInput
            hint={
              task && task.status === "done"
                ? tx("chat.hint.closed")
                : tx("chat.hint.noSession")
            }
            onSend={send}
            sending={sending}
            disabled={!task || task.status === "done" || task.status === "cancelled"}
          />
        ) : null}
      </footer>
    </aside>
  );
}
